import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Alert,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  TrendingUp,
  Water,
  Warning,
  Schedule,
  Analytics,
  Refresh,
  ExpandMore,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Timeline,
  Assessment,
} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {useAuth} from "../../contexts/AuthContext";
import {trackFeatureAccess} from "../../utils/analytics";
import {fetchAnimalGroups} from "../../api/animal";
import {fetchPonds} from "../../api/pond";
import {fetchFarms} from "../../api/farm";
import {
  predictFeedRequirements,
  predictWaterQuality,
  predictMortalityRisk,
  predictOptimalHarvest,
  FeedPredictionResult,
  WaterQualityPrediction,
  MortalityPrediction,
  HarvestPrediction,
} from "../../api/predictive";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const {children, value, index, ...other} = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`predictive-tabpanel-${index}`} aria-labelledby={`predictive-tab-${index}`} {...other}>
      {value === index && <Box sx={{p: 3}}>{children}</Box>}
    </div>
  );
}

interface PredictionsState {
  feed?: FeedPredictionResult;
  waterQuality?: WaterQualityPrediction;
  mortality?: MortalityPrediction;
  harvest?: HarvestPrediction;
}

const PredictiveAnalyticsPage: React.FC = () => {
  const {t} = useTranslation();
  const {user} = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [animalGroups, setAnimalGroups] = useState<any[]>([]);
  const [ponds, setPonds] = useState<any[]>([]);
  const [farms, setFarms] = useState<any[]>([]);
  const [selectedAnimalGroup, setSelectedAnimalGroup] = useState<string>("");
  const [selectedPond, setSelectedPond] = useState<string>("");
  const [daysAhead, setDaysAhead] = useState<number>(30);
  const [predictions, setPredictions] = useState<PredictionsState>({});
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [animals, pondsData, farmsData] = await Promise.all([fetchAnimalGroups(), fetchPonds(), fetchFarms()]);
      setAnimalGroups(animals);
      setPonds(pondsData);
      setFarms(farmsData);
    } catch (error) {
      console.error("Error loading data:", error);
      setError("Failed to load data");
    }
  };

  const predictiveTabNames = [
    "feed_requirements",
    "water_quality",
    "mortality_risk",
    "optimal_harvest",
    "comprehensive_analysis",
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    trackFeatureAccess(`predictive_tab_${predictiveTabNames[newValue] || "unknown"}`, {
      tab_index: newValue,
    });
  };

  const runFeedPrediction = async () => {
    if (!selectedAnimalGroup) {
      setError("Please select an animal group");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await predictFeedRequirements(selectedAnimalGroup, daysAhead);
      setPredictions((prev) => ({...prev, feed: data}));
    } catch (error) {
      setError("Failed to get feed prediction");
      console.error("Feed prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runWaterQualityPrediction = async () => {
    if (!selectedPond) {
      setError("Please select a pond");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await predictWaterQuality(selectedPond, daysAhead);
      setPredictions((prev) => ({...prev, waterQuality: data}));
    } catch (error) {
      setError("Failed to get water quality prediction");
      console.error("Water quality prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runMortalityPrediction = async () => {
    if (!selectedAnimalGroup) {
      setError("Please select an animal group");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await predictMortalityRisk(selectedAnimalGroup);
      setPredictions((prev) => ({...prev, mortality: data}));
    } catch (error) {
      setError("Failed to get mortality prediction");
      console.error("Mortality prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const runHarvestPrediction = async () => {
    if (!selectedAnimalGroup) {
      setError("Please select an animal group");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const data = await predictOptimalHarvest(selectedAnimalGroup);
      setPredictions((prev) => ({...prev, harvest: data}));
    } catch (error) {
      setError("Failed to get harvest prediction");
      console.error("Harvest prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case "low":
        return "success";
      case "medium":
        return "warning";
      case "high":
        return "error";
      default:
        return "info";
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return "success";
    if (confidence >= 60) return "warning";
    return "error";
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{fontWeight: "bold"}}>
          {t("Predictive Analytics")}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="textSecondary">
            {t("AI-powered insights for better decision making")}
          </Typography>
          <Tooltip title={t("Refresh data")}>
            <IconButton onClick={loadData} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 3}} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Paper sx={{width: "100%", mb: 3}}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="predictive analytics tabs">
          <Tab icon={<TrendingUp />} label={t("Feed Requirements")} />
          <Tab icon={<Water />} label={t("Water Quality")} />
          <Tab icon={<Warning />} label={t("Mortality Risk")} />
          <Tab icon={<Schedule />} label={t("Optimal Harvest")} />
          <Tab icon={<Analytics />} label={t("Comprehensive Analysis")} />
        </Tabs>
      </Paper>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title={t("Feed Requirements Prediction")} />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>{t("Animal Group")}</InputLabel>
                  <Select value={selectedAnimalGroup} onChange={(e) => setSelectedAnimalGroup(e.target.value)} label={t("Animal Group")}>
                    {animalGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} - {group.species}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label={t("Days Ahead")}
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(Number(e.target.value))}
                  sx={{mb: 2}}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={runFeedPrediction}
                  disabled={loading || !selectedAnimalGroup}
                  startIcon={<TrendingUp />}>
                  {loading ? t("Calculating...") : t("Predict Feed Requirements")}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {predictions.feed && (
              <Card>
                <CardHeader
                  title={t("Feed Requirements Prediction Results")}
                  action={
                    <Chip
                      label={`${predictions.feed.confidence}% ${t("Confidence")}`}
                      color={getConfidenceColor(predictions.feed.confidence) as any}
                    />
                  }
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.feed.dailyFeedRequirement} kg
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Daily Requirement")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.feed.weeklyFeedRequirement} kg
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Weekly Requirement")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.feed.monthlyFeedRequirement} kg
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Monthly Requirement")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          ${predictions.feed.totalCost}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Total Cost")}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 3}} />

                  <Typography variant="h6" gutterBottom>
                    {t("Key Factors")}
                  </Typography>
                  <List dense>
                    {predictions.feed.factors.map((factor: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText primary={factor} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="h6" gutterBottom sx={{mt: 2}}>
                    {t("Recommendations")}
                  </Typography>
                  <List dense>
                    {predictions.feed.recommendations.map((rec: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title={t("Water Quality Prediction")} />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>{t("Pond")}</InputLabel>
                  <Select value={selectedPond} onChange={(e) => setSelectedPond(e.target.value)} label={t("Pond")}>
                    {ponds.map((pond) => (
                      <MenuItem key={pond.id} value={pond.id}>
                        {pond.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="number"
                  label={t("Days Ahead")}
                  value={daysAhead}
                  onChange={(e) => setDaysAhead(Number(e.target.value))}
                  sx={{mb: 2}}
                />

                <Button fullWidth variant="contained" onClick={runWaterQualityPrediction} disabled={loading || !selectedPond} startIcon={<Water />}>
                  {loading ? t("Calculating...") : t("Predict Water Quality")}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {predictions.waterQuality && (
              <Card>
                <CardHeader
                  title={t("Water Quality Prediction Results")}
                  action={
                    <Box display="flex" gap={1}>
                      <Chip label={predictions.waterQuality.riskLevel} color={getRiskColor(predictions.waterQuality.riskLevel) as any} />
                      <Chip
                        label={`${predictions.waterQuality.confidence}% ${t("Confidence")}`}
                        color={getConfidenceColor(predictions.waterQuality.confidence) as any}
                      />
                    </Box>
                  }
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.waterQuality.temperature}°C
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Predicted Temperature")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.waterQuality.ph}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Predicted pH")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.waterQuality.dissolvedOxygen} mg/L
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Predicted DO")}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 3}} />

                  <Typography variant="h6" gutterBottom>
                    {t("Recommendations")}
                  </Typography>
                  <List dense>
                    {predictions.waterQuality.recommendations.map((rec: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title={t("Mortality Risk Prediction")} />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>{t("Animal Group")}</InputLabel>
                  <Select value={selectedAnimalGroup} onChange={(e) => setSelectedAnimalGroup(e.target.value)} label={t("Animal Group")}>
                    {animalGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} - {group.species}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={runMortalityPrediction}
                  disabled={loading || !selectedAnimalGroup}
                  startIcon={<Warning />}>
                  {loading ? t("Calculating...") : t("Predict Mortality Risk")}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {predictions.mortality && (
              <Card>
                <CardHeader
                  title={t("Mortality Risk Prediction Results")}
                  action={
                    <Chip
                      label={`${predictions.mortality.confidence}% ${t("Confidence")}`}
                      color={getConfidenceColor(predictions.mortality.confidence) as any}
                    />
                  }
                />
                <CardContent>
                  <Paper sx={{p: 2, textAlign: "center", mb: 3}}>
                    <Typography variant="h4" color="error">
                      {predictions.mortality.predictedMortalityRate}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t("Predicted Mortality Rate")}
                    </Typography>
                  </Paper>

                  <Typography variant="h6" gutterBottom>
                    {t("Risk Factors")}
                  </Typography>
                  <List dense>
                    {predictions.mortality.riskFactors.map((factor: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <ErrorIcon color="error" />
                        </ListItemIcon>
                        <ListItemText primary={factor} />
                      </ListItem>
                    ))}
                  </List>

                  <Typography variant="h6" gutterBottom sx={{mt: 2}}>
                    {t("Recommendations")}
                  </Typography>
                  <List dense>
                    {predictions.mortality.recommendations.map((rec: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <CheckCircle color="success" />
                        </ListItemIcon>
                        <ListItemText primary={rec} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title={t("Optimal Harvest Prediction")} />
              <CardContent>
                <FormControl fullWidth sx={{mb: 2}}>
                  <InputLabel>{t("Animal Group")}</InputLabel>
                  <Select value={selectedAnimalGroup} onChange={(e) => setSelectedAnimalGroup(e.target.value)} label={t("Animal Group")}>
                    {animalGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name} - {group.species}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={runHarvestPrediction}
                  disabled={loading || !selectedAnimalGroup}
                  startIcon={<Schedule />}>
                  {loading ? t("Calculating...") : t("Predict Optimal Harvest")}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {predictions.harvest && (
              <Card>
                <CardHeader
                  title={t("Optimal Harvest Prediction Results")}
                  action={
                    <Chip
                      label={`${predictions.harvest.confidence}% ${t("Confidence")}`}
                      color={getConfidenceColor(predictions.harvest.confidence) as any}
                    />
                  }
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {new Date(predictions.harvest.optimalHarvestDate).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Optimal Harvest Date")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.harvest.expectedWeight}g
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Expected Weight")}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{p: 2, textAlign: "center"}}>
                        <Typography variant="h6" color="primary">
                          {predictions.harvest.expectedBiomass} kg
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("Expected Biomass")}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{my: 3}} />

                  <Typography variant="h6" gutterBottom>
                    {t("Key Factors")}
                  </Typography>
                  <List dense>
                    {predictions.harvest.factors.map((factor: string, index: number) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <Info color="info" />
                        </ListItemIcon>
                        <ListItemText primary={factor} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardHeader title={t("Comprehensive Analysis")} />
          <CardContent>
            <Typography variant="body1" color="textSecondary" sx={{mb: 3}}>
              {t("Run comprehensive predictive analysis across multiple animal groups and ponds for strategic planning.")}
            </Typography>

            <Alert severity="info" sx={{mb: 3}}>
              {t("This feature allows you to analyze multiple entities at once for comprehensive insights and planning.")}
            </Alert>

            <Typography variant="h6" gutterBottom>
              {t("Coming Soon")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("Comprehensive analysis features are being developed and will be available soon.")}
            </Typography>
          </CardContent>
        </Card>
      </TabPanel>

      {loading && (
        <Box sx={{width: "100%", mt: 2}}>
          <LinearProgress />
        </Box>
      )}
    </Box>
  );
};

export default PredictiveAnalyticsPage;
